import * as React from 'react';

const isServerSide = typeof window === 'undefined';

interface OnIdleProps {
  onIdle?: React.ComponentType<any>;
}

const useShouldRender = () => {
  const [shouldRender, setShouldRender] = React.useState(false);

  // console.info('is idle', id, shouldRender, Date.now());
  React.useEffect(() => {
    let timeOutId;
    let requestCallbackId;
    if (!('requestIdleCallback' in window)) {
      // console.info('use setTimeout');
      timeOutId = setTimeout(() => setShouldRender(true), 500);
    } else {
      // console.info('use requestCallback');
      requestCallbackId = requestIdleCallback(
        () => requestAnimationFrame(() => setShouldRender(true)),
        {
          timeout: 2000,
        }
      );
    }

    return () => {
      if (timeOutId) {
        clearTimeout(timeOutId);
      }
      if (requestCallbackId) {
        cancelIdleCallback(requestCallbackId);
      }
    };
  }, []);

  return shouldRender;
};

/**
 * Component using this HOC should be rendered on client-side only ( watch out Hydration miss-match if using on SSR as using media query for rendering )
 * @param fromScreen
 * @param toScreen
 */
export const withShouldRenderOnIdle = (opts?: OnIdleProps) => (Component: any) => (props: any) => {
  const shouldRender = useShouldRender();

  if (shouldRender) {
    // This component should be wrapped with dynamic import function
    return <Component {...props} />;
  }

  if (opts?.onIdle) {
    const IdleComponent = opts.onIdle;
    return <IdleComponent {...props} />;
  }

  return null;
};

export const shouldRenderOnIdle =
  <P extends React.PropsWithoutRef<any>>(opts?: OnIdleProps) =>
  (Component: React.ComponentType<P>): React.ComponentType<P> => {
    if (isServerSide) {
      if (opts?.onIdle) {
        return opts.onIdle;
      }
      return () => null;
    }

    // Client side check
    return withShouldRenderOnIdle(opts)(Component);
  };
